import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock, TrendingUp } from "lucide-react";
import SEOHead, { personSchema, websiteSchema } from "../components/SEOHead";
import { HomepageConversationSection } from "../components/FeedbackSections";
import { API_BASE_URL } from "../lib/site";

const API = API_BASE_URL;
const NEWSLETTER_BG = "https://images.pexels.com/photos/13551572/pexels-photo-13551572.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect width='400' height='250' fill='%23F3F4F6'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239CA3AF' font-family='sans-serif' font-size='14'%3ENo image%3C/text%3E%3C/svg%3E";

const isComingSoonArticle = (article) =>
  !article.content || (article.tags || []).some((tag) => tag.toLowerCase() === "coming soon");

const isComingSoonCaseStudy = (caseStudy) => caseStudy.presentation_mode === "coming-soon";
const shouldContainCaseStudyCover = (caseStudy) => caseStudy.cover_image?.includes("notion-zoho-product-led-cover");

const SC_DEFAULTS = {
  hero_headline_line1: "Building brands",
  hero_headline_line2: "consumers trust.",
  hero_subheading: "~10 years across Amazon, Nestle, Zepto, Dabur and Emami. Now advising B2C startups on integrated growth — brand, performance, and CRM as one system.",
  hero_bottom_label: "~10 years building",
  hero_monogram_subtitle: "Amazon · Nestle · Zepto · Dabur · Emami",
  hero_stats: [
    { value: "~20%", label: "MoM growth at Zepto", accent: true },
    { value: "₹40Cr+", label: "Monthly budget managed", accent: false },
    { value: "22%", label: "CAC reduction at Amazon", accent: false },
    { value: "43%", label: "ROI improvement, FMCG", accent: true },
  ],
  marquee_items: [
    { value: "~10 Years", label: "Experience" },
    { value: "₹40Cr+", label: "Monthly budgets managed" },
    { value: "43%", label: "Avg. ROI improvement" },
    { value: "2x", label: "Category growth delivered" },
    { value: "Amazon • Nestle • Zepto • Dabur • Emami", label: "Career highlights" },
  ],
  hero_cta_primary: "Work with me",
  hero_cta_secondary: "Read my writing",
  articles_section_label: "Knowledge",
  articles_section_heading: "Latest Thinking",
  articles_link_text: "All writing",
  case_studies_section_label: "Evidence",
  case_studies_section_heading: "Case Studies",
};

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribers`, { email, source: "homepage" });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="relative overflow-hidden border-t border-[#E5E7EB]" data-testid="newsletter-section">
      <div className="absolute inset-0">
        <img src={NEWSLETTER_BG} alt="" className="w-full h-full object-cover opacity-5" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20 flex flex-col items-center text-center">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">Newsletter</p>
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-4 max-w-xl">
          Marketing frameworks for founders who think
        </h2>
        <p className="text-[#4B5563] mb-8 max-w-md leading-relaxed">
          Sharp, actionable insights on brand strategy, D2C growth, and performance marketing. No fluff.
        </p>
        {status === "success" ? (
          <div className="flex items-center gap-2 text-[#002FA7] font-medium">
            <TrendingUp size={18} /> You're subscribed! Look out for the first issue.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
              className="flex-1 border border-[#E5E7EB] px-4 py-3 text-sm focus:outline-none focus:border-[#002FA7] transition-colors" data-testid="homepage-email-input" />
            <button type="submit" className="bg-[#002FA7] text-white px-6 py-3 text-sm font-semibold hover:bg-[#001D66] transition-colors whitespace-nowrap" data-testid="homepage-subscribe-btn">Subscribe</button>
          </form>
        )}
        {status === "error" && <p className="text-red-500 text-xs mt-2">Something went wrong. Try again.</p>}
      </div>
    </section>
  );
}

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [sc, setSc] = useState(SC_DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => setArticles(r.data.filter((article) => !isComingSoonArticle(article)).slice(0, 2))).catch(() => {});
    axios.get(`${API}/case-studies`).then((r) => setCaseStudies(r.data.filter((caseStudy) => !isComingSoonCaseStudy(caseStudy)).slice(0, 2))).catch(() => {});
    axios.get(`${API}/public/site-content`).then((r) => setSc({ ...SC_DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  const heroStats = sc.hero_stats || SC_DEFAULTS.hero_stats;
  const marqueeItems = sc.marquee_items || SC_DEFAULTS.marquee_items;

  return (
    <div className="page-enter">
      <SEOHead schema={[personSchema, websiteSchema]} />

      {/* ─── HERO ─── */}
      <section className="border-b border-[#E5E7EB]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,420px)] gap-12 lg:gap-16 items-start lg:items-center pt-24 pb-12 sm:pt-28 sm:pb-16 lg:pt-32 lg:pb-20">
          <div className="fade-in-up">
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-6">
              Growth & Marketing Consultant
            </p>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl xl:text-6xl font-light text-[#0A0A0A] leading-[1.05] tracking-tight mb-6">
              {sc.hero_headline_line1}
              <br />
              <span className="font-semibold">{sc.hero_headline_line2}</span>
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed mb-8 max-w-lg">
              {sc.hero_subheading}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/book" className="bg-[#002FA7] text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#001D66] transition-colors flex items-center gap-2 justify-center" data-testid="hero-book-btn">
                {sc.hero_cta_primary} <ArrowRight size={16} />
              </Link>
              <Link to="/writing" className="border border-[#0A0A0A] text-[#0A0A0A] px-7 py-3.5 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors text-center">
                {sc.hero_cta_secondary}
              </Link>
            </div>
          </div>

          <div className="relative fade-in-up-delay-2 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-[26rem]">
              <div className="w-full flex flex-col gap-3" data-testid="hero-stats-panel">
                {/* Monogram card */}
                <div className="bg-[#002FA7] text-white p-7 flex items-center gap-5">
                  <div className="w-16 h-16 border-2 border-white/30 flex items-center justify-center font-['Outfit'] font-bold text-2xl flex-shrink-0">NP</div>
                  <div>
                    <p className="font-['Outfit'] font-semibold text-xl">Niharika Patel</p>
                    <p className="text-white/70 text-sm mt-0.5">Growth & Marketing Consultant</p>
                    <p className="text-white/50 text-xs mt-1">{sc.hero_monogram_subtitle}</p>
                  </div>
                </div>
                {/* Stats 2x2 */}
                <div className="grid grid-cols-2 gap-3">
                  {heroStats.map((s, i) => (
                    <div key={i} className={`p-4 border ${s.accent ? "border-[#002FA7] bg-[#002FA7]/4" : "border-[#E5E7EB] bg-white"}`}>
                      <p className={`font-['Outfit'] text-2xl font-bold ${s.accent ? "text-[#002FA7]" : "text-[#0A0A0A]"}`}>{s.value}</p>
                      <p className="text-xs text-[#6B7280] mt-0.5 leading-snug">{s.label}</p>
                    </div>
                  ))}
                </div>
                {/* Bottom strip */}
                <div className="border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 flex items-center justify-between">
                  <p className="text-xs text-[#9CA3AF] uppercase tracking-wide font-medium">{sc.hero_bottom_label}</p>
                  <div className="flex gap-1">
                    {["A", "N", "Z", "D", "E"].map((l, i) => (
                      <div key={i} className="w-6 h-6 bg-[#E5E7EB] flex items-center justify-center text-[10px] font-bold text-[#4B5563]">{l}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── MARQUEE ─── */}
      <div className="border-b border-[#E5E7EB] py-4 overflow-hidden bg-[#F9FAFB]">
        <div className="marquee-track">
          {[...marqueeItems, ...marqueeItems].map((s, i) => (
            <span key={i} className="flex items-center gap-6 mx-8 text-sm text-[#4B5563] whitespace-nowrap">
              <span className="font-['Outfit'] font-semibold text-[#002FA7] text-base">{s.value}</span>
              <span className="uppercase tracking-[0.12em] text-[10px] text-[#9CA3AF]">{s.label}</span>
              <span className="w-1 h-1 bg-[#D1D5DB] rounded-full" />
            </span>
          ))}
        </div>
      </div>

      {/* ─── LATEST THINKING ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20" data-testid="articles-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-2">{sc.articles_section_label}</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-medium text-[#0A0A0A]">{sc.articles_section_heading}</h2>
          </div>
          <Link to="/writing" className="text-sm font-medium text-[#002FA7] flex items-center gap-1 hover:gap-2 transition-all">{sc.articles_link_text} <ArrowRight size={14} /></Link>
        </div>
        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.map((article, i) => (
              <Link key={article.id} to={`/writing/${article.slug}`} className={`group block border border-[#E5E7EB] card-hover ${i === 0 ? "md:col-span-2 md:row-span-1" : ""}`} data-testid={`article-card-${i}`}>
                <div className={`overflow-hidden bg-[#F3F4F6] ${i === 0 ? "aspect-[16/7]" : "aspect-video"}`}>
                  <img src={article.cover_image || PLACEHOLDER_IMG} alt={article.title} className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500" onError={(e) => { e.target.src = PLACEHOLDER_IMG; }} />
                </div>
                <div className="p-5">
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {(article.tags || []).slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#002FA7] bg-blue-50 px-2 py-0.5">{tag}</span>
                    ))}
                  </div>
                  <h3 className="font-['Outfit'] font-semibold text-[#0A0A0A] text-lg leading-tight mb-2 group-hover:text-[#002FA7] transition-colors">{article.title}</h3>
                  <p className="text-sm text-[#6B7280] leading-relaxed line-clamp-2">{article.excerpt}</p>
                  <div className="flex items-center gap-4 mt-4 text-xs text-[#9CA3AF]">
                    <span className="flex items-center gap-1"><Clock size={12} /> {article.read_time} min read</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="border border-[#E5E7EB] p-12 text-center text-[#9CA3AF]">Articles coming soon.</div>
        )}
      </section>

      {/* ─── CASE STUDIES ─── */}
      <section className="border-t border-[#E5E7EB] bg-[#F9FAFB]" data-testid="case-studies-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-2">{sc.case_studies_section_label}</p>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-medium text-[#0A0A0A]">{sc.case_studies_section_heading}</h2>
            </div>
            <Link to="/case-studies" className="text-sm font-medium text-[#002FA7] flex items-center gap-1 hover:gap-2 transition-all">All case studies <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.map((cs, i) => (
              <div
                key={cs.id}
                className={`bg-white border border-[#E5E7EB] card-hover flex flex-col ${i === 0 ? "md:col-span-2" : ""}`}
              >
                <div className={`overflow-hidden ${shouldContainCaseStudyCover(cs) ? "bg-[#8FB6B5]" : "bg-[#F3F4F6]"} ${i === 0 && !shouldContainCaseStudyCover(cs) ? "aspect-[16/8]" : "aspect-video"}`}>
                  <img
                    src={cs.cover_image || PLACEHOLDER_IMG}
                    alt={cs.title}
                    className={`w-full h-full ${shouldContainCaseStudyCover(cs) ? "object-contain" : "object-cover"}`}
                    onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                  />
                </div>

                <div className="p-5 md:p-6 flex flex-col flex-1">
                  <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#6B7280] mb-3">{cs.industry}</p>
                  <h3 className={`font-['Outfit'] font-semibold text-[#0A0A0A] leading-tight mb-3 ${i === 0 ? "text-2xl" : "text-xl"}`}>
                    {cs.title}
                  </h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed mb-5">{cs.description}</p>

                  <div className={`grid gap-3 ${i === 0 ? "grid-cols-2 xl:grid-cols-4" : "grid-cols-2"} mb-5`}>
                    {(cs.metrics || []).slice(0, 4).map((m, metricIndex) => (
                      <div key={metricIndex} className="border border-[#E5E7EB] p-3 bg-[#F9FAFB]">
                        <p className={`font-['Outfit'] font-bold text-[#002FA7] ${i === 0 ? "text-xl" : "text-lg"}`}>{m.value}</p>
                        <p className="text-[11px] text-[#6B7280] mt-0.5">{m.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto">
                    <Link
                      to={`/case-studies/${cs.id}`}
                      className="text-sm font-semibold text-[#002FA7] inline-flex items-center gap-2 hover:gap-3 transition-all"
                    >
                      Read case study <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ABOUT STRIP ─── */}
      <section className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 sm:py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-4">About</p>
            <h2 className="font-['Outfit'] text-3xl font-medium text-[#0A0A0A] mb-5">What I bring is a combination rarely found in one profile.</h2>
            <p className="text-[#4B5563] leading-relaxed mb-5">I've built growth systems at the world's largest e-commerce platform, led category marketing for FMCG icons, and driven hyper-growth at a quick-commerce unicorn. Now I bring that accumulated pattern recognition to early-stage B2C startups.</p>
            <Link to="/about" className="text-sm font-semibold text-[#002FA7] flex items-center gap-2 hover:gap-3 transition-all">Full story <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Brand Strategy", desc: "Positioning, narrative & equity building" },
              { label: "Performance Marketing", desc: "Meta, Google, affiliate & attribution" },
              { label: "CRM & Lifecycle", desc: "Retention, repeat purchase & loyalty" },
              { label: "AI in Marketing", desc: "Leveraging AI for growth & scale" },
            ].map((s) => (
              <div key={s.label} className="border border-[#E5E7EB] p-4">
                <p className="font-['Outfit'] font-semibold text-[#0A0A0A] text-sm mb-1">{s.label}</p>
                <p className="text-xs text-[#6B7280] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomepageConversationSection />

      {/* ─── NEWSLETTER ─── */}
      <NewsletterSection />
    </div>
  );
}
